import { plugin as $date } from '@/plugins/date'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { ReservationServiceModel, ReservationServiceDiscountType } from '@/_api/models/reservationServiceModel'
import { ServiceAssociationDiscountType } from '@/_api/models/serviceAssociation'
import { ServiceResourceAssignment } from '@/_api/models/serviceResource'
import { AvailabilityRequestGroupMode } from '@/_api/models/booking'
import { ServiceCustomerType } from '@/_api/models/service'
import { ReservationCustomerRequestType } from '@/_api/models/reservation'
import { CustomerPrivacyConsent } from '@/_api/models/customer'
import {
  RecurrenceIntervalType,
  RecurrenceIntervalMonthType,
  RecurrenceWeeks,
  RecurrenceMonths,
  RecurrenceEndingType,
  Recurrence,
} from '@/_api/models/recurrence'

export class StagingRequest {
  constructor (context, { customerType = ServiceCustomerType.single, presets = {} }) {
    this.context = context

    this.customerType = customerType

    this.presets = presets

    this.waitlist = presets.waitlist || null
    this.move = presets.move || null

    this.customer = presets.customer || null
    this.services = presets.services || []
    this.notes = ''
    this.customFields = null

    this.availabilitySlot = null
    this.overrideTime = false

    this.customerMax = presets.move?.customerMax || 0
    this.allowInternet = true

    this.recurrence = new StagingRecurrence()

    this.rebookReservation = presets.rebookReservation || null
    this.rebookReservationCustomer = presets.rebookReservationCustomer || null

    this.validation = null

    this.sendEmail = this.context?.$auth.info?.location?.sendInternalReservationCreateNotifications
    this.resetReminder = true
    this.customerRequestType = ReservationCustomerRequestType.none

    if (!this.hasService) {
      this.addReservationService()
    }

    for (const reservationService of this.services) {
      reservationService.setValues()
    }
  }

  get options () {
    const { customer, reservationCustomer, notes, customFields, customerType, sendEmail, resetReminder, customerRequestType, overrideTime, customerMax, allowInternet, move, recurrence, rebookReservation, rebookReservationCustomer, waitlist } = this

    return {
      customFields,
      customer,
      reservationCustomer: this.hasGroupService ? reservationCustomer || customer : null,
      notes,
      customerType,
      sendEmail,
      resetReminder,
      customerRequestType,
      overrideTime,
      customerMax,
      allowInternet,
      future: this.recurrence.enabled,
      move,
      recurrence: !this.hasGroupService || this.isCustomerTypeGroup ? recurrence.reservationOptions : null,
      customerRecurrence: this.hasGroupService && this.isCustomerTypeSingle ? recurrence.customerOptions : null,
      waitlist,
      rebookReservation,
      rebookReservationCustomer,
    }
  }

  get progress () {
    if (!this.isCompleteCustomer) {
      return 1
    }

    if (!this.isCompleteServices) {
      return 2
    }

    if (!this.isCompleteAvailability) {
      return 3
    }

    if (!this.isCompleteRecurrence) {
      return 4
    }

    return 7
  }

  get groupMode () {
    return this.isCustomerTypeGroup || this.hasSingleService ? AvailabilityRequestGroupMode.reservation : AvailabilityRequestGroupMode.customer
  }

  get isCustomerTypeSingle () {
    return this.customerType === ServiceCustomerType.single
  }

  get isCustomerTypeGroup () {
    return this.customerType === ServiceCustomerType.group
  }

  get isCompleteCustomer () {
    const customer = this.customer

    if (this.isCustomerTypeGroup) {
      return true
    }

    if (customer) {
      if (customer.isNewRecord) {
        const customerPrivacyConsentEnabled = this.context?.$auth.info?.location?.customerConsentEnabled
        return !!customer.firstName.length && (!customerPrivacyConsentEnabled || customer.consentAccepted === CustomerPrivacyConsent.accepted)
      } else {
        return true
      }
    }

    return false
  }

  get isCompleteServices () {
    const services = this.services
    const service = services[0]
    return !!service && !!service.service && (!!service.employee || service.employee === 0)
  }

  get isCompleteAvailability () {
    return !!this.availabilitySlot
  }

  get isCompleteRecurrence () {
    return !this.recurrence.isEnabled || this.recurrence.isComplete
  }

  get isManualAvailability () {
    return this.mode === 'manual'
  }

  get isNewCustomer () {
    return this.customer && this.customer.isNewRecord
  }

  get hasService () {
    return !!this.services.length
  }

  get hasSingleService () {
    return this.hasService && this.services.some(service => service.service && service.service.customerType === ServiceCustomerType.single)
  }

  get hasGroupService () {
    return this.hasService && this.services.some(service => service.service && service.service.customerType === ServiceCustomerType.group)
  }

  get isEmployeeRequest () {
    return this.hasService && this.services.some(service => service.service && service.employee !== 0)
  }

  fromReservation (reservation) {
    this.services = []

    for (const reservationService of reservation.services) {
      this.addReservationService(reservationService)
    }

    if (reservation.recurrence) {
      this.recurrence.fromReservation(reservation)
      this.recurrence.enabled = false
    }

    if (reservation.notes) {
      this.notes = reservation.notes
    }
  }

  fromWaitlist (waitlist) {
    this.services = []

    if (waitlist.dates) {
      const date = waitlist.dates[0]?.date

      if (date) {
        this.presets.date = date ? $date.utc(new Date(date * 1000)) : new Date()
      }
    }

    for (const reservationService of waitlist.services) {
      this.addReservationService(reservationService)
    }
  }

  fromMove (reservation) {
    this.customer = reservation.customer
    this.reservationCustomer = reservation
    this.notes = reservation.notes
    this.customFields = reservation.customFields
    this.customerType = reservation.customerType
    this.services = []

    this.sendEmail = this.context?.$auth.info?.location?.sendInternalReservationMoveNotifications

    this.presets.startTime = reservation.startTime?.value || reservation.reservation?.startTime?.value
    this.presets.date = this.context.$date.parse(reservation.date?.value || reservation.reservation?.date?.value || new Date()) // reservation.date.value

    if (reservation.service) {
      this.addReservationService(reservation)
    } else {
      for (const reservationService of reservation.services) {
        this.addReservationService(reservationService)
      }
    }

    if (reservation.recurrence) {
      this.recurrence.fromReservation(reservation)
      this.recurrence.enabled = false
    }
  }

  addReservationService (data = {}) {
    const reservationService = new ReservationServiceModel()

    const idService = data.service?.idService || data.service?.id || this.presets.service?.idService
    const idEmployee = data.employee?.idEmployee || data.employee?.id || this.presets.employee?.idEmployee

    // Store lookups can miss for roles without service:view or employee:view.
    // Fall back to the reservation payload so moving/editing does not drop resources.
    const service = idService ? (useServiceStore().getServiceByID(idService) || data.service || null) : null
    const employee = idEmployee ? (useEmployeeStore().getEmployeeByID(idEmployee) || data.employee || 0) : 0

    reservationService.service = service
    reservationService.employee = employee

    reservationService.setValues(data)

    if (data.association) {
      this.applyAssociation(reservationService, data.association)
    }

    const assignment = this.isCustomerTypeGroup || reservationService.isServiceCustomerTypeSingle
      ? ServiceResourceAssignment.reservation
      : ServiceResourceAssignment.customer

    reservationService.setResources(assignment, data.resources)

    this.services.push(reservationService)
  }

  applyAssociation (reservationService, association) {
    reservationService.association = association
    reservationService.idAssociation = association?.idAssociation

    // Preserve any selected timings/resources while resetting a persisted or
    // manually overridden price to the canonical base before discounting it.
    reservationService.setValues({
      startTime: reservationService.startTime,
      duration: reservationService.duration,
      processing: reservationService.processing,
      finishing: reservationService.finishing,
      bufferTimePre: reservationService.bufferTimePre,
      bufferTimePost: reservationService.bufferTimePost,
      allowInternet: reservationService.allowInternet,
      customerMax: reservationService.customerMax,
      price: null,
    })

    reservationService.discount = Number(association?.discountAmount || 0)
    reservationService.discountType = association?.discountType === ServiceAssociationDiscountType.Percentage
      ? ReservationServiceDiscountType.percentage
      : ReservationServiceDiscountType.fixed
  }
}

export class StagingRecurrence {
  constructor () {
    this.enabled = false
    this.preview = null
    this.reservations = []
    this.recurrence = {
      repeat: {
        interval: RecurrenceIntervalType.week,
        frequency: 1,
        on: {
          week: {
            days: [1],
          },
          month: {
            type: RecurrenceIntervalMonthType.day,
            weeks: [RecurrenceWeeks.first],
            days: [1],
            dates: [1],
          },
          year: {
            date: { date: 1, month: RecurrenceMonths.january },
          },
        },
      },
      ending: {
        type: RecurrenceEndingType.repeats,
        date: $date.format($date.add(new Date(), { years: 1, days: -21 })),
        repeats: 5,
      },
    }
  }

  fromReservation (reservation) {
    const recurrence = reservation.recurrence

    if (recurrence) {
      this.recurrence.repeat.frequency = recurrence.repeat.frequency

      if (recurrence.isRecurrenceIntervalWeek) {
        this.recurrence.repeat.interval = RecurrenceIntervalType.week
        this.recurrence.repeat.on.week.days = recurrence.repeat.on.values.map(value => Number(value))
      }

      if (recurrence.isRecurrenceIntervalMonth) {
        this.recurrence.repeat.interval = RecurrenceIntervalType.month

        if (recurrence.isRecurrenceMonthTypeDate) {
          this.recurrence.repeat.on.month.type = RecurrenceIntervalMonthType.date
          this.recurrence.repeat.on.month.days = recurrence.repeat.on.values.map(value => Number(value))
        }

        if (recurrence.isRecurrenceMonthTypeDay) {
          this.recurrence.repeat.on.month.type = RecurrenceIntervalMonthType.day
          this.recurrence.repeat.on.month.days = recurrence.repeat.on.values.map(value => Number(value))
          this.recurrence.repeat.on.month.weeks = recurrence.repeat.on.options.weeksOfMonth
        }

        if (recurrence.isRecurrenceMonthTypeLast) {
          this.recurrence.repeat.on.month.type = RecurrenceIntervalMonthType.last
        }
      }

      if (recurrence.isRecurrenceIntervalYear) {
        this.recurrence.repeat.interval = RecurrenceIntervalType.year
        const { date, month } = recurrence.repeat.on.values[0]
        this.recurrence.repeat.on.year.date.date = date
        this.recurrence.repeat.on.year.date.month = month
      }

      if (recurrence.isEndingOnDate) {
        this.recurrence.ending.type = RecurrenceEndingType.date
        this.recurrence.ending.date = recurrence.ending.date.value
      }

      if (recurrence.isEndingAfterRepeats) {
        this.recurrence.ending.type = RecurrenceEndingType.repeats
        this.recurrence.ending.repeats = recurrence.ending.numberOfRepeats
      }
    } else if (reservation.date?.date instanceof Date) {
      // No existing recurrence (e.g. "Make recurring" on an existing class):
      // default the weekday to the reservation's own day. reservation.date is a
      // DateYMD whose `.date` getter returns a LOCAL-midnight Date, so use
      // getDay() (local) — getUTCDay() would be off-by-one in UTC+ timezones.
      let day = reservation.date.date.getDay()

      if (day === 0) {
        day = 7
      }

      this.recurrence.repeat.on.week.days = [day]
      this.recurrence.repeat.on.month.days = [day]
    }
  }

  setDayOfWeek (date) {
    if (date instanceof Date) {
      let day = date.getUTCDay()

      if (day === 0) {
        day = 7
      }

      this.recurrence.repeat.on.week.days = [day]
      this.recurrence.repeat.on.month.days = [day]
    }
  }

  get reservationOptions () {
    if (this.enabled && this.preview) {
      const recurrence = this.recurrence
      const preview = this.preview

      const dates = preview.recurrence.selectedDates

      const datesWithConflicts = dates
        .filter(date => date.hasConflicts)

      return new Recurrence({ ...recurrence, datesWithConflicts })
    }

    return null
  }

  get customerOptions () {
    if (this.enabled && this.reservations) {
      const reservations = this.reservations
        .map(({ reservation, action }) => {
          return {
            idReservation: reservation.idReservation,
            date: reservation.date,
            action: action.value,
          }
        })

      return { reservations }
    }

    return undefined
  }

  get isEnabled () {
    return this.enabled
  }

  get isComplete () {
    const dates = this.preview?.recurrence?.selectedDates

    if (dates) {
      const conflicts = dates
        .filter(date => date.hasConflicts)
        .some(date => !date.option)

      return !conflicts
    }

    return this.reservations
  }
}
